import Button from "@mui/material/Button";
import DownloadIcon from "@mui/icons-material/Download";
import axiosGenerico from "../utils/axiosGenerico";
import { useCargando } from "../context/cargando-context";
import { useSnackbar } from "notistack";

/**
 * Componente para descargar archivos de cualquier tipo
 * @param {{
 *   url: string,
 *   nombreArchivo: string,
 *   titulo: string,
 *   icono?: React.ReactNode,
 *   getParams?: () => object,
 *   propiedades?: object,
 *   timeout?: number
 * }} Props
 */
export const BotonDescarga = ({
  url,
  nombreArchivo,
  titulo,
  icono = <DownloadIcon />,
  getParams = () => ({}),
  propiedades = {},
  timeout = 60000,
}) => {
  const { empezarCarga, terminarCarga } = useCargando();
  const { enqueueSnackbar } = useSnackbar();

  const handleDownload = async () => {
    try {
      empezarCarga();
      const params = getParams();
      const urlFinal = buildUrl(url, params);

      const response = await axiosGenerico.get(urlFinal, {
        responseType: "blob",
        timeout,
      });

      // Obtener nombre de archivo desde headers
      const contentDisposition = response.headers["content-disposition"];
      let fileName = nombreArchivo;

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(
          /filename\*?=['"]?(?:UTF-\d['"]*)?([^;'"]+)['"]?/i
        );
        if (fileNameMatch?.[1]) {
          fileName = fileNameMatch[1];
        }
      }

      // Obtener tipo MIME desde headers
      const contentType =
        response.headers["content-type"] || "application/octet-stream";

      // Crear y descargar archivo
      const blob = new Blob([response.data], { type: contentType });
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();

      // Limpiar recursos
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error en descarga:", error);
      enqueueSnackbar("Error al descargar archivo", { variant: "error" });
      // Aquí puedes agregar manejo de errores personalizado
    } finally {
      terminarCarga();
    }
  };

  return (
    <Button
      fullWidth
      variant="text"
      size="small"
      startIcon={icono}
      onClick={handleDownload}
      {...propiedades}
    >
      {titulo}
    </Button>
  );
};

type QueryParamValue = string | number | boolean;

const buildUrl = (
  baseUrl: string,
  params?: Record<string, QueryParamValue>
): string => {
  const queryString = Object.entries(params || {})
    .map(([key, value]) => {
      const encodedValue = encodeURIComponent(
        typeof value === "boolean" ? (value ? "1" : "0") : value.toString()
      );
      return `${encodeURIComponent(key)}=${encodedValue}`;
    })
    .join("&");

  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};
